import { useState, useEffect } from 'react';
import { Pencil, Trash2, Send, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import InviteUserPanel from '../components/Admin/InviteUserPanel';
import apiService from '../services/apiService';
import KnowledgeApprovalPanel from './KnowledgeApprovalPanel';
import { theme, rgba } from '../theme';

const ROLE_RANK = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
const STATUS_COLORS = { active:'#22c55e', pending:'#f59e0b', suspended:'#ef4444', inactive:'#64748b', incomplete:'#f97316' };
const STATUS_LABELS = { incomplete:'Incomplete Setup' };

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState({ status:'all', role:'all', search:'' });
  const [showInvite, setShowInvite] = useState(false);
  const [activeTab, setActiveTab]   = useState('team');
  const [pendingCount, setPendingCount] = useState(0);
  const [editingId, setEditingId]   = useState(null);
  const [editForm, setEditForm]     = useState({ role:'', status:'' });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [inviteTargetId, setInviteTargetId] = useState(null);
  const [inviteEmailDraft, setInviteEmailDraft] = useState('');
  // The read-only demo account: GET /auth/users answers with sample accounts and demo: true
  // (the backend never sends it the real login accounts). No invite/edit/delete/resend then.
  const [isDemo, setIsDemo] = useState(false);

  const myRank = ROLE_RANK[user?.role] ?? 0;
  const canInvite = myRank >= ROLE_RANK['manager'] && !isDemo;

  const canManage = (target) => {
    if (isDemo || !target || target._id === user?._id) return false;
    return (ROLE_RANK[target.role] ?? 0) < myRank;
  };

  const loadUsers = async () => {
    try {
      const data = await apiService.request('/auth/users');
      setUsers(data.users || []);
      setIsDemo(data.demo === true);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => {
    apiService.request('/api/knowledge/pending')
      .then(data => setPendingCount((data.items || []).length))
      .catch(() => {});
  }, []);

  const flashMessage = (text, isError = false) => {
    setActionMessage({ text, isError });
    setTimeout(() => setActionMessage(null), 4000);
  };

  const startEdit = (u) => {
    setEditingId(u._id);
    setEditForm({ role: u.role, status: u.status });
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id) => {
    setBusyId(id);
    try {
      await apiService.updateUser(id, editForm);
      flashMessage('User updated.');
      setEditingId(null);
      await loadUsers();
    } catch (err) {
      flashMessage(err.message || 'Update failed.', true);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = (u) => {
    setConfirmDeleteId(u._id);
    setEditingId(null);
  };

  const doDelete = async (id) => {
    setBusyId(id);
    try {
      await apiService.deleteUser(id);
      flashMessage('User removed.');
      setConfirmDeleteId(null);
      await loadUsers();
    } catch (err) {
      flashMessage(err.message || 'Delete failed.', true);
    } finally {
      setBusyId(null);
    }
  };

  const doResend = async (id) => {
    setBusyId(id);
    try {
      const res = await apiService.resendInvite(id);
      flashMessage(res.emailSent ? 'Invite resent.' : `Invite regenerated. Code: ${res.tempCode}`);
    } catch (err) {
      flashMessage(err.message || 'Resend failed.', true);
    } finally {
      setBusyId(null);
    }
  };

  const startInvite = (u) => {
    if (u.email) {
      doSendInvite(u);
    } else {
      setInviteTargetId(u._id);
      setInviteEmailDraft('');
    }
  };

  const cancelInvite = () => setInviteTargetId(null);

  const doSendInvite = async (u, email) => {
    setBusyId(u._id);
    try {
      const res = await apiService.inviteEmployee(u.employeeId, email || u.email);
      flashMessage(res.emailSent ? 'Invite sent.' : `Invite created. Code: ${res.tempCode}`);
      setInviteTargetId(null);
      await loadUsers();
    } catch (err) {
      flashMessage(err.message || 'Invite failed.', true);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = users.filter(u => {
    if (filter.status !== 'all' && u.status !== filter.status) return false;
    if (filter.role   !== 'all' && u.role   !== filter.role)   return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.toLowerCase();
      if (!name.includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Management</h1>
          <p style={styles.sub}>{users.length} total members</p>
        </div>
        {canInvite && (
          <button style={styles.inviteBtn} onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Close' : '+ Invite Member'}
          </button>
        )}
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        <button onClick={() => setActiveTab('team')} style={{ padding:'8px 20px', borderRadius:6, border:'none', background: activeTab==='team' ? '#6366f1' : '#1e293b', color: activeTab==='team' ? '#fff' : '#94a3b8', fontWeight:600, cursor:'pointer' }}>
          Team Members
        </button>
        <button onClick={() => setActiveTab('approvals')} style={{ padding:'8px 20px', borderRadius:6, border:'none', background: activeTab==='approvals' ? '#6366f1' : '#1e293b', color: activeTab==='approvals' ? '#fff' : '#94a3b8', fontWeight:600, cursor:'pointer' }}>
          Knowledge Queue{pendingCount > 0 && <span style={{ background:'#dc2626', color:'#fff', borderRadius:20, fontSize:11, padding:'1px 7px', marginLeft:6 }}>{pendingCount}</span>}
        </button>
      </div>

      {activeTab === 'team' ? (
        <>
          {isDemo && (
            <div style={styles.demoBanner}>
              Demo: sample accounts shown - real login accounts are hidden.
            </div>
          )}
          {showInvite && (
            <div style={{marginBottom:'32px'}}>
              <InviteUserPanel onInvited={() => { loadUsers(); setShowInvite(false); }} />
            </div>
          )}

          {actionMessage && (
            <div style={{
              marginBottom:16, padding:'10px 16px', borderRadius:8, fontSize:13,
              background: actionMessage.isError ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
              color: actionMessage.isError ? '#ef4444' : '#22c55e',
              border: `1px solid ${actionMessage.isError ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
            }}>
              {actionMessage.text}
            </div>
          )}

          <div style={styles.filters}>
            <input
              type="text"
              placeholder="Search name or email..."
              value={filter.search}
              onChange={e => setFilter({...filter, search:e.target.value})}
              style={{...styles.select, minWidth:'220px'}}
            />
            <select style={styles.select} value={filter.status} onChange={e => setFilter({...filter, status:e.target.value})}>
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="incomplete">Incomplete Setup</option>
            </select>
            <select style={styles.select} value={filter.role} onChange={e => setFilter({...filter, role:e.target.value})}>
              <option value="all">All Roles</option>
              <option value="employee">Employee</option>
              <option value="analyst">Analyst</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {loading ? (
            <p style={{color:'#94a3b8'}}>Loading...</p>
          ) : (
            <div style={styles.list}>
              {filtered.map(u => {
                const isEditing = editingId === u._id;
                const isConfirmingDelete = confirmDeleteId === u._id;
                const manageable = canManage(u);
                const isBusy = busyId === u._id;

                const isIncomplete = u.status === 'incomplete';
                const isInviting = inviteTargetId === u._id;

                return (
                  <div key={u._id} style={styles.card}>
                    <div style={styles.avatar}>
                      {(u.profile?.firstName?.[0] || u.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={styles.name}>{u.profile?.firstName} {u.profile?.lastName}{u.jobTitle ? ` · ${u.jobTitle}` : ''}</div>
                      <div style={styles.email}>{u.email || 'No email on file'}</div>
                    </div>

                    <div style={styles.lastLogin}>
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'}
                    </div>

                    {isIncomplete ? (
                      <>
                        <div style={{...styles.badge, background: STATUS_COLORS.incomplete + '33', color: STATUS_COLORS.incomplete}}>
                          {STATUS_LABELS.incomplete}
                        </div>
                        {isInviting ? (
                          <>
                            <input
                              type="email"
                              placeholder="Email address"
                              value={inviteEmailDraft}
                              onChange={e => setInviteEmailDraft(e.target.value)}
                              style={{...styles.select, minWidth:'200px'}}
                              autoFocus
                            />
                            <button
                              onClick={() => doSendInvite(u, inviteEmailDraft)}
                              disabled={isBusy || !inviteEmailDraft.includes('@')}
                              style={styles.iconBtnGood}
                              title="Send invite"
                            >
                              <Check size={16} />
                            </button>
                            <button onClick={cancelInvite} disabled={isBusy} style={styles.iconBtnNeutral} title="Cancel">
                              <X size={16} />
                            </button>
                          </>
                        ) : canInvite && (
                          <button onClick={() => startInvite(u)} disabled={isBusy} style={styles.iconBtnNeutral} title="Send invite">
                            <Send size={15} />
                          </button>
                        )}
                      </>
                    ) : isEditing ? (
                      <>
                        <select
                          value={editForm.role}
                          onChange={e => setEditForm({...editForm, role:e.target.value})}
                          style={{...styles.select, minWidth:'110px'}}
                        >
                          <option value="employee">Employee</option>
                          <option value="analyst">Analyst</option>
                          <option value="manager">Manager</option>
                          {myRank >= ROLE_RANK['admin'] + 1 && <option value="admin">Admin</option>}
                        </select>
                        <select
                          value={editForm.status}
                          onChange={e => setEditForm({...editForm, status:e.target.value})}
                          style={{...styles.select, minWidth:'100px'}}
                        >
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="suspended">Suspended</option>
                        </select>
                        <button onClick={() => saveEdit(u._id)} disabled={isBusy} style={styles.iconBtnGood} title="Save">
                          <Check size={16} />
                        </button>
                        <button onClick={cancelEdit} disabled={isBusy} style={styles.iconBtnNeutral} title="Cancel">
                          <X size={16} />
                        </button>
                      </>
                    ) : isConfirmingDelete ? (
                      <>
                        <span style={{color:'#ef4444', fontSize:13}}>Delete this user?</span>
                        <button onClick={() => doDelete(u._id)} disabled={isBusy} style={styles.iconBtnBad} title="Confirm delete">
                          <Check size={16} />
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} disabled={isBusy} style={styles.iconBtnNeutral} title="Cancel">
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={styles.role}>{u.role}</div>
                        <div style={{...styles.badge, background: STATUS_COLORS[u.status] + '33', color: STATUS_COLORS[u.status]}}>
                          {u.status}
                        </div>
                        {u.status === 'pending' && manageable && (
                          <button onClick={() => doResend(u._id)} disabled={isBusy} style={styles.iconBtnNeutral} title="Resend invite">
                            <Send size={15} />
                          </button>
                        )}
                        {manageable && (
                          <>
                            <button onClick={() => startEdit(u)} disabled={isBusy} style={styles.iconBtnNeutral} title="Edit">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => confirmDelete(u)} disabled={isBusy} style={styles.iconBtnNeutral} title="Delete">
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p style={{color:'#64748b',textAlign:'center',padding:'32px'}}>No users match the current filters.</p>
              )}
            </div>
          )}
        </>
      ) : (
        <KnowledgeApprovalPanel />
      )}
    </div>
  );
}

const styles = {
  page:      { padding:'32px', minHeight:'100vh', background:`linear-gradient(135deg,${rgba(theme.colors.primaryRgb, 0.05)},${rgba(theme.colors.accentRgb, 0.05)})` },
  header:    { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' },
  title:     { color:'#e2e8f0', fontSize:'28px', fontWeight:'700', margin:0 },
  sub:       { color: theme.colors.textOnPageBackground, fontSize:'14px', marginTop:'4px' },
  inviteBtn: { padding:'12px 24px', background:'linear-gradient(135deg,#6C63FF,#3b82f6)', border:'none', borderRadius:'8px', color:'white', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  filters:   { display:'flex', gap:'12px', marginBottom:'24px' },
  select:    { padding:'10px 16px', background:'rgba(30,41,59,0.8)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'8px', color:'#e2e8f0', fontSize:'14px', outline:'none' },
  list:      { display:'flex', flexDirection:'column', gap:'12px' },
  demoBanner: { marginBottom:16, padding:'10px 16px', borderRadius:8, fontSize:13, background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.3)' },
  card:      { display:'flex', alignItems:'center', gap:'16px', padding:'16px 20px', background:'rgba(15,23,42,0.8)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'12px' },
  avatar:    { width:'40px', height:'40px', borderRadius:'50%', background:'linear-gradient(135deg,#6C63FF,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'700', fontSize:'16px', flexShrink:0 },
  name:      { color:'#e2e8f0', fontWeight:'600', fontSize:'15px' },
  email:     { color:'#64748b', fontSize:'13px', marginTop:'2px' },
  lastLogin: { color:'#64748b', fontSize:'12px', minWidth:'140px', textAlign:'right', flexShrink:0 },
  role:      { color:'#94a3b8', fontSize:'13px', textTransform:'capitalize', minWidth:'70px', textAlign:'right' },
  badge:     { padding:'4px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600', textTransform:'capitalize' },
  iconBtnNeutral: { background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', color:'#3b82f6', padding:'6px', display:'flex', alignItems:'center', cursor:'pointer' },
  iconBtnGood:    { background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'6px', color:'#22c55e', padding:'6px', display:'flex', alignItems:'center', cursor:'pointer' },
  iconBtnBad:     { background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'6px', color:'#ef4444', padding:'6px', display:'flex', alignItems:'center', cursor:'pointer' },
};
